// nfeParse.js — Parsing de NF-e brasileira no frontend.
//
// D-39/D-40: extração de campos do XML da NF-e via DOMParser nativo do browser.
// D-45: sem dependências npm novas — função pura, sem React, sem rede.
// T-08-37 (anti-XXE): DOMParser nativo do browser NÃO resolve DTD/entidades externas
// por padrão. Nenhuma lib XML externa, nenhum eval/innerHTML do conteúdo do XML.
//
// Contrato de saída de parseNfeXml(xmlString):
//   {
//     supplier: string|null,   // emit/xNome
//     cnpj: string|null,       // emit/CNPJ (só dígitos)
//     nfNumber: string|null,   // ide/nNF
//     nfKey: string|null,      // 44 dígitos do Id (sem "NFe"), ou null
//     nfDate: string|null,     // ide/dhEmi (fallback: ide/dEmi)
//     items: [{ nItem, desc, quantity, unitValue, totalValue, unit }]
//   }
//   // lança Error('NF-e inválida: ...') em XML malformado ou sem infNFe.
//
// NOTA: tags consultadas via getElementsByTagName (insensível a namespace),
// NÃO querySelector — compatível com DOMParser application/xml e @xmldom/xmldom.

/**
 * Extrai os campos relevantes de uma NF-e brasileira (layout 4.00) a partir
 * do seu XML como string.
 *
 * @param {string} xmlString - conteúdo XML da NF-e
 * @returns {{ supplier, cnpj, nfNumber, nfKey, nfDate, items }}
 * @throws {Error} se o XML for malformado ou não contiver infNFe
 */
export function parseNfeXml(xmlString) {
  // Parse — DOMParser nativo do browser (não resolve entidades externas por padrão).
  const doc = new DOMParser().parseFromString(xmlString, 'application/xml');

  // Detecção de XML malformado: DOMParser retorna <parsererror> em vez de lançar.
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('NF-e inválida: XML malformado.');
  }

  // Localizar infNFe — raiz canônica. Funciona com nfeProc/NFe/infNFe e NFe/infNFe.
  const inf = doc.getElementsByTagName('infNFe')[0];
  if (!inf) {
    throw new Error('NF-e inválida: estrutura infNFe não encontrada.');
  }

  // Helper: texto do primeiro filho com a tag dada, dentro de um elemento.
  const text = (el, tag) => {
    const n = el?.getElementsByTagName(tag)?.[0];
    return n ? (n.textContent || '').trim() : null;
  };

  // ── Fornecedor (emit) ────────────────────────────────────────────────────────
  // D-40: pegar do PRIMEIRO emit (não do destinatário, que fica em dest).
  const emit = inf.getElementsByTagName('emit')[0] || inf;
  const supplier = text(emit, 'xNome');
  const rawCnpj = text(emit, 'CNPJ');
  // Normalizar CNPJ: apenas dígitos (remove pontos, barras, hífens).
  const cnpj = rawCnpj != null ? rawCnpj.replace(/\D/g, '') : null;

  // ── Número e data de emissão (ide) ──────────────────────────────────────────
  const ide = inf.getElementsByTagName('ide')[0] || inf;
  const nfNumber = text(ide, 'nNF');
  const nfDate = text(ide, 'dhEmi') || text(ide, 'dEmi');

  // ── Chave de acesso (atributo Id de infNFe) ─────────────────────────────────
  // Id = "NFe" + 44 dígitos. Extraímos os últimos 44 dígitos.
  const rawId = inf.getAttribute && inf.getAttribute('Id');
  const digits = rawId ? rawId.replace(/\D/g, '') : '';
  const nfKey = digits.length >= 44 ? digits.slice(-44) : null;

  // ── Itens (det/prod) ────────────────────────────────────────────────────────
  // D-40: extrair todos os itens — a seleção do item diesel fica na UI (08-11).
  const dets = inf.getElementsByTagName('det');
  const items = [];
  for (let i = 0; i < dets.length; i++) {
    const det = dets[i];
    const prod = det.getElementsByTagName('prod')[0];
    if (!prod) continue;
    items.push({
      nItem: Number(det.getAttribute('nItem')) || (i + 1),
      desc: text(prod, 'xProd'),
      quantity: parseFloat(text(prod, 'qCom')),
      unitValue: parseFloat(text(prod, 'vUnCom')),
      totalValue: parseFloat(text(prod, 'vProd')),
      unit: text(prod, 'uCom'),
    });
  }

  return { supplier, cnpj, nfNumber, nfKey, nfDate, items };
}

/**
 * Converte um File para base64 puro (sem prefixo data-URL), para
 * armazenamento direto no registro de combustível (D-43).
 *
 * @param {File} file - arquivo a converter
 * @returns {Promise<string>} base64 puro
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(r.error || new Error('Falha ao ler arquivo'));
    r.readAsDataURL(file);
  });
}
